var __assign = (this && this.__assign) || Object.assign || function(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
        s = arguments[i];
        for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
            t[p] = s[p];
    }
    return t;
};
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { of as observableOf } from 'rxjs';
import { map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { MatDialog } from '@angular/material';
import { LoopBackAuth, OrganisationApi, OrganisationsApi } from '@careapp/angular-sdk';
import { ADMIN_ROLE, DASHBOARD_ROLES, highestRole, ORGANISATION_ROLE_HEIRARCHY } from '@careapp/core';
import { Actions, Effect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ConfirmationDialogComponent } from '../../shared/confirmation-dialog/confirmation-dialog.component';
import { Organisations, OrgContextChange } from '../actions';
import * as selectors from '../reducers';
var OrganisationEffects = /** @class */ (function () {
    function OrganisationEffects(store, actions$, api, adminApi, auth, dialog) {
        var _this = this;
        this.store = store;
        this.actions$ = actions$;
        this.api = api;
        this.adminApi = adminApi;
        this.auth = auth;
        this.dialog = dialog;
        this.findUserOrganisations = this.actions$.ofType(Organisations.REQUEST_OWN).pipe(withLatestFrom(this.store.select(selectors.auth.isAdmin)), switchMap(function (_a) {
            var action = _a[0], isAdmin = _a[1];
            return _this.api
                .listOrganisations()
                .map(function (payload) {
                if (!isAdmin) {
                    payload = payload.filter(function (value) {
                        return DASHBOARD_ROLES.includes(highestRole(value.roles, [ADMIN_ROLE].concat(ORGANISATION_ROLE_HEIRARCHY)));
                    });
                }
                return Organisations.ResponseSuccessOwn(payload);
            })
                .catch(function (err) {
                return observableOf(Organisations.ResponseFailOwn(err));
            });
        }));
        this.saveOrganisation = this.actions$.ofType(Organisations.REQUEST_SAVE).pipe(switchMap(function (action) {
            return _this.adminApi
                .updateAttributes(action.payload.id, action.payload.data)
                .map(function (payload) {
                return Organisations.ResponseSuccessSaveAction(payload);
            })
                .catch(function (err) {
                return observableOf(Organisations.ResponseFailSaveAction(err));
            });
        }));
        this.getOrganisations = this.actions$.ofType(Organisations.REQUEST_ALL).pipe(switchMap(function (action) {
            return _this.adminApi
                .find()
                .map(function (payload) {
                return Organisations.ResponseSuccessAllAction(payload);
            })
                .catch(function (err) {
                return observableOf(Organisations.ResponseFailAllAction(err));
            });
        }));
        this.organisationDetail = this.actions$.ofType(Organisations.REQUEST_BY_ID).pipe(switchMap(function (action) {
            return _this.adminApi
                .findById(action.payload.id)
                .map(function (payload) {
                return Organisations.ResponseSuccessByIdAction(payload);
            })
                .catch(function (err) {
                return observableOf(Organisations.ResponseFailByIdAction(err));
            });
        }));
        this.createOrganisation = this.actions$.ofType(Organisations.REQUEST_CREATE).pipe(switchMap(function (action) {
            return _this.adminApi
                .create(action.payload)
                .map(function (response) {
                return Organisations.ResponseSuccessCreateAction(response);
            })
                .catch(function (err) {
                return observableOf(Organisations.ResponseFailCreateAction(err));
            });
        }));
        this.refreshOrgsAfterCreate = this.actions$
            .ofType(Organisations.RESPONSE_SUCCESS_CREATE)
            .pipe(map(function (action) { return Organisations.RequestOwn(); }));
        this.getPreferences = this.actions$.ofType(Organisations.REQUEST_PREFERENCES).pipe(withLatestFrom(this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS))), switchMap(function (_a) {
            var action = _a[0], id = _a[1];
            return _this.api
                .getOrganisationPreference(id)
                .map(function (res) { return Organisations.ResponseSuccessPreferences(__assign({}, res, { organisationId: id })); })
                .catch(function (err) { return observableOf(Organisations.ResponseFailPreferences(err)); });
        }));
        this.updatePreferences = this.actions$.ofType(Organisations.UPDATE_PREFERENCES).pipe(withLatestFrom(this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS))), mergeMap(function (_a) {
            var action = _a[0], id = _a[1];
            return _this.api
                .setOrganisationPreference(id, action.payload)
                .map(function (res) { return Organisations.SuccessUpdatePreferences(__assign({}, res, { organisationId: id })); })
                .catch(function (err) { return observableOf(Organisations.FailUpdatePreferences(err)); });
        }));
        this.getBranding = this.actions$.ofType(Organisations.REQUEST_BRANDING).pipe(withLatestFrom(this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS))), switchMap(function (_a) {
            var action = _a[0], id = _a[1];
            return _this.api
                .getBranding(id)
                .map(function (res) { return Organisations.ResponseSuccessBranding(__assign({}, res, { organisationId: id })); })
                .catch(function (err) { return observableOf(Organisations.ResponseFailBranding(err)); });
        }));
        this.updateBrandingColors = this.actions$.ofType(Organisations.UPDATE_BRANDING_COLORS).pipe(withLatestFrom(this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS))), switchMap(function (_a) {
            var action = _a[0], id = _a[1];
            return _this.api
                .updateBrandingColors(id, action.payload)
                .map(function (res) { return Organisations.SuccessUpdateBrandingColors(__assign({}, res, { organisationId: id })); })
                .catch(function (err) { return observableOf(Organisations.FailUpdateBrandingColors(err)); });
        }));
        this.resetBrandingColors = this.actions$.ofType(Organisations.RESET_BRANDING_COLORS).pipe(withLatestFrom(this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS))), switchMap(function (_a) {
            var action = _a[0], id = _a[1];
            return _this.dialog
                .open(ConfirmationDialogComponent, {
                data: {
                    title: 'Reset Colors?',
                    body: 'Are you sure you want to reset your branding colors to the app defaults? This can not be undone',
                    confirm: 'Reset',
                    cancel: 'Cancel',
                    confirmColor: 'warn'
                }
            })
                .afterClosed()
                .pipe(map(function (result) { return [action, id, result]; }));
        }), switchMap(function (_a) {
            var action = _a[0], id = _a[1], result = _a[2];
            return result
                ? _this.api
                    .updateBrandingColors(id, action.payload)
                    .map(function (res) { return Organisations.SuccessUpdateBrandingColors(__assign({}, res, { organisationId: id })); })
                    .catch(function (err) { return observableOf(Organisations.FailUpdateBrandingColors(err)); })
                : observableOf({ type: 'CANCELLED' });
        }));
        this.removeBrandingImage = this.actions$.ofType(Organisations.REMOVE_BRANDING_IMAGE).pipe(withLatestFrom(this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS))), switchMap(function (_a) {
            var action = _a[0], id = _a[1];
            return _this.dialog
                .open(ConfirmationDialogComponent, {
                data: {
                    title: 'Reset Image?',
                    body: 'Are you sure you want to reset this image to the application default? This can not be undone',
                    confirm: 'Reset',
                    cancel: 'Cancel',
                    confirmColor: 'warn'
                }
            })
                .afterClosed()
                .pipe(map(function (result) { return [action, id, result]; }));
        }), switchMap(function (_a) {
            var action = _a[0], id = _a[1], result = _a[2];
            return result
                ? _this.api
                    .updateBrandingMedia(id, __assign({}, action.payload, { purpose: null }))
                    .map(function (res) { return Organisations.SuccessUpdateBrandingImage(__assign({}, res, { organisationId: id })); })
                    .catch(function (err) { return observableOf(Organisations.FailUpdateBrandingImage(err)); })
                : observableOf({ type: 'CANCELLED' });
        }));
        this.updateBrandingMedia = this.actions$.ofType(Organisations.UPDATE_BRANDING_IMAGE).pipe(withLatestFrom(this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS))), switchMap(function (_a) {
            var action = _a[0], id = _a[1];
            return _this.api
                .updateBrandingMedia(id, action.payload)
                .map(function (res) { return Organisations.SuccessUpdateBrandingImage(__assign({}, res, { organisationId: id })); })
                .catch(function (err) { return observableOf(Organisations.FailUpdateBrandingImage(err)); });
        }));
        this.contextSwitch = this.actions$
            .ofType(Organisations.SELECT_BY_ID)
            .pipe(map(function () { return OrgContextChange(); }));
    }
    __decorate([
        Effect(),
        __metadata("design:type", Object)
    ], OrganisationEffects.prototype, "findUserOrganisations", void 0);
    __decorate([
        Effect(),
        __metadata("design:type", Object)
    ], OrganisationEffects.prototype, "saveOrganisation", void 0);
    __decorate([
        Effect(),
        __metadata("design:type", Object)
    ], OrganisationEffects.prototype, "getOrganisations", void 0);
    __decorate([
        Effect(),
        __metadata("design:type", Object)
    ], OrganisationEffects.prototype, "organisationDetail", void 0);
    __decorate([
        Effect(),
        __metadata("design:type", Object)
    ], OrganisationEffects.prototype, "createOrganisation", void 0);
    __decorate([
        Effect(),
        __metadata("design:type", Object)
    ], OrganisationEffects.prototype, "refreshOrgsAfterCreate", void 0);
    __decorate([
        Effect(),
        __metadata("design:type", Object)
    ], OrganisationEffects.prototype, "getPreferences", void 0);
    __decorate([
        Effect(),
        __metadata("design:type", Object)
    ], OrganisationEffects.prototype, "updatePreferences", void 0);
    __decorate([
        Effect(),
        __metadata("design:type", Object)
    ], OrganisationEffects.prototype, "getBranding", void 0);
    __decorate([
        Effect(),
        __metadata("design:type", Object)
    ], OrganisationEffects.prototype, "updateBrandingColors", void 0);
    __decorate([
        Effect(),
        __metadata("design:type", Object)
    ], OrganisationEffects.prototype, "resetBrandingColors", void 0);
    __decorate([
        Effect(),
        __metadata("design:type", Object)
    ], OrganisationEffects.prototype, "removeBrandingImage", void 0);
    __decorate([
        Effect(),
        __metadata("design:type", Object)
    ], OrganisationEffects.prototype, "updateBrandingMedia", void 0);
    __decorate([
        Effect(),
        __metadata("design:type", Object)
    ], OrganisationEffects.prototype, "contextSwitch", void 0);
    return OrganisationEffects;
}());
export { OrganisationEffects };
